'use client';

import { cn } from '@ngg/storefront-utils';
import Link from '@ui/components/Link/Link';
import { Button } from '@ngg/components';
import { useMemo } from 'react';
import * as styles from './PagePagination.styles';
import { useSearchParams } from 'next/navigation';
import config from '@utils/config';

type Props = {
  pageRange: number;
  page: number;
  totalPages: number;
  slug: string;
  goToPage: string;
};
export default function PageLinkButtons(props: Props) {
  const { pageRange, page, totalPages, slug, goToPage } = props;
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();

  const pages = useMemo(() => {
    // Show all pages if pageRange is 0
    if (pageRange === 0) return Array.from({ length: totalPages }, (_, i) => i);
    const start = Math.max(0, page - pageRange);
    const end = Math.min(totalPages, page + pageRange + 1);

    // Create array from start to end
    return Array.from({ length: end - start }, (_, i) => i + start + 1);
  }, [page, totalPages, pageRange]);

  return (
    <ul className={styles.pageRange()}>
      {page > pageRange && (
        <>
          <li>
            <Button
              as={Link}
              locale={config.locale}
              href={`${slug}${
                searchParamsString ? `?${searchParamsString}` : ''
              }`}
              className={styles.digits()}
              aria-label={`${goToPage} 1`}>
              1
            </Button>
          </li>
          {page > pageRange + 1 && (
            <li aria-hidden className={cn(styles.ellipsis())}>
              ...
            </li>
          )}
        </>
      )}

      {pages.map((currentPage) => (
        <li key={currentPage}>
          <Button
            as={Link}
            locale={config.locale}
            href={
              currentPage > 1
                ? `${slug}/${currentPage}${
                    searchParamsString ? `?${searchParamsString}` : ''
                  }`
                : `${slug}${searchParamsString ? `?${searchParamsString}` : ''}`
            }
            disabled={page === currentPage}
            aria-current={page === currentPage}
            data-headlessui-state={page === currentPage ? 'active' : ''}
            aria-label={`${goToPage} ${currentPage}`}
            className={styles.digits()}>
            {currentPage}
          </Button>
        </li>
      ))}

      {page < totalPages - pageRange - 1 && (
        <>
          <li aria-hidden className={cn('h-8 w-8 text-center leading-[2rem]')}>
            ...
          </li>
          <li>
            <Button
              as={Link}
              locale={config.locale}
              href={`${slug}/${totalPages}${
                searchParamsString ? `?${searchParamsString}` : ''
              }`}
              className={cn([styles.lastDigit()])}
              aria-label={`${goToPage} ${totalPages}`}>
              {totalPages}
            </Button>
          </li>
        </>
      )}
    </ul>
  );
}
